'use client';
import { Surface } from '@payments-checkout/shared/components';
import { ItemShape, ItemShapeProps, Tag, Text } from '@stone-payments/jade';
import { useIntl } from 'react-intl';

type PaymentMethodCard = {
  paymentType: 'credit_card' | 'pix' | 'boleto';
  isSelected: boolean;
  selectPaymentMethod: any;
  hasDiscount: boolean;
  maxInstallments?: number;
  disabled?: boolean;
};

const icon: Record<PaymentMethodCard['paymentType'], ItemShapeProps['use']> = {
  credit_card: 'card',
  pix: 'brand-pix',
  boleto: 'barcode',
};

export function PaymentMethodCard({
  paymentType,
  isSelected,
  selectPaymentMethod,
  hasDiscount,
  maxInstallments,
  disabled = false,
}: PaymentMethodCard) {
  const intl = useIntl();

  return (
    <Surface
      onClick={() => selectPaymentMethod(paymentType)}
      hasPadding={false}
      className={`${isSelected
        ? '!bg-jade-background-brand-subtle-hover'
        : 'bg-jade-background-surface'
        }
      ${isSelected
          ? '!border-jade-border-brand'
          : 'border border-jade-border-low'
        }
      ${disabled && 'opacity-50 cursor-not-allowed'}
      shadow-sm !p-jade-150  border-solid border border-jade-border-low cursor-pointer`}
      role="radio"
      data-testid={`payment-selection-${paymentType}`}
      aria-checked={isSelected}
    >
      <div className="w-full flex items-center justify-between">
        <div className='flex items-center gap-jade-200'>
          <ItemShape
            size="small"
            use={icon[paymentType] as any}
            variant={isSelected ? 'brand' : 'neutral'}
          />
          <Text
            variant="text-large"
            weight="bold"
            color={isSelected ? 'brand' : 'high'}
          >
            {intl.formatMessage({ id: 'paymentMethod.' + paymentType })}
          </Text>
        </div>
        {hasDiscount && (
          <Tag size="medium" variant="brand" solid={isSelected} className="whitespace-nowrap">
            {intl.formatMessage({ id: 'hasDiscount' }).toLowerCase()}
          </Tag>
        )}
        {!!maxInstallments && maxInstallments > 1 && (
          <Tag size="medium" variant="brand" solid={isSelected} className="whitespace-nowrap">
            {intl.formatMessage({ id: 'hasInstallments' }, { max: maxInstallments }).toLowerCase()}
          </Tag>
        )}
      </div>
    </Surface>
  );
}
